<template>
  <div class="dashboard" :class="{ 'sidebar-collapsed': sidebarCollapsed }">
    <AppNavigation
      mode="sidebar"
      class="nav-panel"
      @add-language="showAddLanguage = true"
      @upgrade-required="showMultiLangUpsell = true"
    />
    <main class="content-area">
      <RouterView v-slot="{ Component }">
        <Transition :name="transitionName" :css="isMobile">
          <component :is="Component" :key="$route.path" />
        </Transition>
      </RouterView>
    </main>

    <!-- Keep onboarding modals at root level -->
    <div v-if="showLanguageConflictModal">
      <LanguageConflictModal
        :selected-language="studentInfo.selectedLanguage"
        @resolved="handleConflictResolved"
      />
    </div>
    <div v-if="showOnboarding">
      <OnboardingWizard
        :initial-step="onboardingInitialStep"
        :prefill-name="onboardingPrefillName"
        :prefill-native-language="onboardingPrefillNativeLanguage"
        :prefill-selected-language="onboardingPrefillLanguage"
        :user-data="wizardUserData"
        @completed="handleOnboardingCompleted"
      />
    </div>

    <!-- Multi-language modals. Which one opens is decided by the paid
         entitlement (features.multiLingual); no feature flag is involved. -->
    <AddLanguageFlow v-model="showAddLanguage" />
    <MultiLanguageUpsellModal v-model="showMultiLangUpsell" />

    <!-- One-time mobile-launch bonus explainer. Hosted here because this is
         the authenticated landing surface and because the modal-stacking
         state it has to yield to lives in this component. -->
    <FreebiesExplainerModal
      :model-value="showFreebiesModal"
      :chats-bonus="freebiesBonus ? freebiesBonus.chats : 0"
      :lessons-bonus="freebiesBonus ? freebiesBonus.lessons : 0"
      @update:model-value="onFreebiesModalUpdate"
    />
  </div>
</template>

<script>
import Languages from "@/data/Language.js";
import OnboardingWizard from "@/components/OnboardingWizard.vue";
import LanguageConflictModal from "@/components/LanguageConflictModal.vue";
import AppNavigation from "@/components/AppNavigation.vue";
import AddLanguageFlow from "@/components/AddLanguageFlow.vue";
import MultiLanguageUpsellModal from "@/components/MultiLanguageUpsellModal.vue";
import FreebiesExplainerModal from "@/components/FreebiesExplainerModal.vue";
import { authStore } from "@/store/auth.js";
import { flagsStore } from "@/store/countryFlags.js";
import i18n, { mapLanguageNameToLocale } from "@/locales/index.js";
import {
  getDatabase,
  ref,
  onValue,
  update,
} from "firebase/database";
import { stripeUtils } from "@/scripts/stripeUtils.js";
import {
  getFreebiesBonus,
  hasFreebiesGrant,
} from "@/composables/useUsageCounts.js";
import { useTheme } from "@/composables/useTheme.js";
import { adoptStoredPlaybackRate } from "@/scripts/playbackRate.js";
import { useToast } from "@/composables/useToast.js";
import { platformBilling } from "@/scripts/platformBilling.js";
import { iapUtils } from "@/scripts/iapUtils.js";
import { pricingStore } from "@/store/pricing.js";
import { STORAGE_KEYS } from "@/constants/storageKeys.js";
import {
  markCheckoutLaunched,
  wasCheckoutLaunched,
} from "@/store/checkoutSession.js";
// Deliberately the standalone state module and not useAccountDeletion: the
// composable would drag firebase/auth, authUtils (router, push notifications,
// admin store) and cloudFunctions into this view, none of which it imports
// today. accountDeletionState imports nothing at all.
import { isAccountDeletionInFlight } from "@/composables/accountDeletionState.js";

// How many times one mount may retry a failing startup write (region
// detection, timezone save) before giving up until the next mount. Bounds the
// retries a persistently failing endpoint can drive from the snapshot stream.
const STARTUP_WRITE_MAX_ATTEMPTS = 3;
// Retries are scheduled rather than waiting for the next users/{uid} snapshot:
// a failed write changes nothing, so onValue may never fire again. Multiplied
// by the attempt number for a simple backoff.
const STARTUP_WRITE_RETRY_BASE_MS = 5000;

// The onboarding plan step reports its billing cycle the Stripe way
// ("month"/"year"), the spelling AccountView and MultiLanguageUpsellModal
// already split out of a planType. The store rails speak "monthly"/"yearly".
// The translation is a lookup rather than a ternary on purpose: BOTH of the
// helpers involved read anything that is not the exact yearly spelling as
// monthly, so an unrecognised cycle would silently sell the monthly plan
// instead of failing. Here an unknown cycle resolves to nothing at all.
const CHECKOUT_CYCLES = {
  month: { lookupKeyField: "monthlyLookupKey", iapCycle: "monthly" },
  year: { lookupKeyField: "yearlyLookupKey", iapCycle: "yearly" },
};

// Mirrors helpers/users.isFreeUser exactly: `"features" not in userData or
// userData["features"] is None`. A truthiness test would read a present but
// falsy value (`false`, `0`, `""` from legacy or malformed data) as free,
// while the server calls that account paid and never applies freebies.
const isFreeAccount = (userData) => {
  const features = Object.prototype.hasOwnProperty.call(userData, "features")
    ? userData.features
    : undefined;
  return features === undefined || features === null;
};

export default {
  name: "DashboardView",
  components: {
    AppNavigation,
    OnboardingWizard,
    LanguageConflictModal,
    AddLanguageFlow,
    MultiLanguageUpsellModal,
    FreebiesExplainerModal,
  },
  data() {
    return {
      showOnboarding: false,
      // The wizard is on screen AND has not reported itself done. While this
      // is true the wizard owns the surface: no snapshot may dismiss it and
      // none of the post-onboarding side effects may run behind it. Cleared
      // only by the wizard's own completed event (and by a re-bind to a
      // different account).
      onboardingInFlight: false,
      onboardingInitialStep: 1,
      onboardingPrefillName: "",
      onboardingPrefillNativeLanguage: "",
      onboardingPrefillLanguage: null,
      showLanguageConflictModal: false,
      // Multi-language modals; toggled by the switcher surfaces' add-language
      // / upgrade-required events (desktop sidebar dropdown, mobile tab-bar sheet).
      showAddLanguage: false,
      showMultiLangUpsell: false,
      // Mobile-launch bonus explainer: this account holds a grant the server
      // would honor and has not recorded a dismissal yet. Whether it is
      // actually on screen is showFreebiesModal, which also yields to any
      // other modal this view hosts.
      freebiesEligible: false,
      // The coerced grant, or null. Only ever produced by the shared helper.
      freebiesBonus: null,
      // Whether this subscription's account has already dismissed the
      // explainer. RTDB keeps streaming users/{uid} while the flag write is in
      // flight, and an unrelated field can change in that window; without this
      // latch the next snapshot would re-derive "granted and unseen" and pop
      // the explainer straight back open. Cleared by watchStudentInfo when it
      // binds to a different account.
      freebiesDismissed: false,
      studentInfo: -1,
      changeLanguageDisabled: true,
      languageFlagSrc: flagsStore.getFlag("unknown"),
      languageFlagAltText: "Country flag for selected language",
      sidebarCollapsed: false,
      transitionName: "slide-left",
      isMobile: window.innerWidth <= 768,
    };
  },
  computed: {
    // Stacking two scrims leaves the user with two dismissals to find and
    // breaks the focus trap, so the explainer yields to every modal THIS VIEW
    // hosts (a child route can still open one of its own; the plan scopes the
    // rule to the onboarding wizard and the language-conflict modal). Kept as
    // a computed rather than a one-shot decision so the explainer appears by
    // itself the moment the blocker closes, instead of being lost for the
    // session.
    freebiesModalBlocked() {
      return (
        this.showOnboarding ||
        this.showLanguageConflictModal ||
        this.showAddLanguage ||
        this.showMultiLangUpsell
      );
    },
    showFreebiesModal() {
      return this.freebiesEligible && !this.freebiesModalBlocked;
    },
    // studentInfo is -1 before the first snapshot and null for an account
    // whose node does not exist yet; the wizard's plan step reads real
    // fields off it, so hand it an object or nothing.
    wizardUserData() {
      const data = this.studentInfo;
      return data && typeof data === "object" ? data : null;
    },
  },
  watch: {
    $route(to, from) {
      const toDepth = to.meta.depth ?? 0;
      const fromDepth = from.meta.depth ?? 0;
      this.transitionName = toDepth >= fromDepth ? "slide-left" : "slide-right";
    },
    studentInfo(newVal) {
      // Before the branching below: the language-conflict branch returns
      // early, and eligibility is independent of which onboarding state the
      // profile is in. Suppression is handled by showFreebiesModal, which
      // reads the modal flags this watcher goes on to set.
      this.evaluateFreebiesGrant(newVal);
      // A wizard mid-flow is dismissed by its own completed event and by
      // nothing else. Step 2's writes land here as a snapshot carrying both
      // name and selectedLanguage — the exact shape this view reads as
      // "onboarding is over" — so applying it would unmount the wizard before
      // its plan step could render and start the ongoing-chat work behind the
      // modal. Nothing is lost: handleOnboardingCompleted replays the latest
      // snapshot the moment the wizard is actually done.
      if (this.onboardingInFlight) return;
      this.applyStudentInfo(newVal);
    },
  },
  methods: {
    // The studentInfo watcher's body, callable on its own so the snapshot
    // withheld while the wizard was mid-flow can be replayed on completion.
    applyStudentInfo(newVal) {
      // This account is being deleted by this session right now, so the
      // snapshot describes a node being torn down, not a person to onboard.
      // The check covers EVERY snapshot shape, not just null: a partial
      // resurrection like {region: ...} is non-null, has no `name`, and would
      // open the wizard through the same branch. Nothing else derived from the
      // profile is worth applying either — the account is going away, and its
      // dialogs read would only race the deletion.
      if (isAccountDeletionInFlight()) {
        console.debug("account deletion in flight; ignoring users/ snapshot");
        return;
      }
      if (newVal === null || !("name" in newVal)) {
        console.debug("showing onboarding wizard at step 1");
        this.onboardingInitialStep = 1;
        this.onboardingPrefillName = "";
        this.onboardingPrefillNativeLanguage = "";
        this.openOnboarding();
      } else {
        this.showOnboarding = false;

        // Check for language conflict BEFORE any other checks
        if (this.hasLanguageConflict(newVal)) {
          console.debug("detected language conflict");
          this.showLanguageConflictModal = true;
          return; // Block everything else until fixed
        }

        if ("selectedLanguage" in newVal) {
          console.debug("selectedLanguage exists. enabling buttons");
          this.changeLanguageDisabled = false;
          this.languageFlagSrc = flagsStore.getFlag(newVal.selectedLanguage);
          this.languageFlagAltText = Languages[newVal.selectedLanguage].altText;
        } else {
          console.debug("showing onboarding wizard at step 2");
          this.onboardingInitialStep = 2;
          this.onboardingPrefillName = newVal.name || "";
          this.onboardingPrefillNativeLanguage = newVal.nativeLanguage || "";
          this.openOnboarding();
        }
      }
    },
    // Opening the wizard is also what starts its mid-flow guard: from here
    // until its completed event, the snapshot stream cannot take it away.
    openOnboarding() {
      this.showOnboarding = true;
      this.onboardingInFlight = true;
    },
    hasLanguageConflict(userData) {
      if (!userData.nativeLanguage || !userData.selectedLanguage) {
        return false;
      }
      // Map nativeLanguage display name to code for comparison
      const nativeCode = mapLanguageNameToLocale(userData.nativeLanguage);
      return nativeCode === userData.selectedLanguage;
    },
    // `planSelection` is the wizard's {plan, cycle} report of what the user
    // picked. It is a request, not an entitlement: nothing here writes
    // features, and a checkout that is cancelled, fails or never confirms
    // leaves a working free account behind.
    handleOnboardingCompleted(planSelection) {
      this.onboardingInFlight = false;
      this.onboardingPrefillLanguage = null;
      this.showOnboarding = false;
      // Snapshots that arrived while the wizard was mid-flow were withheld,
      // and step 2's own write is normally one of them, so the work behind
      // them has to be replayed here or it never runs for this session.
      //
      // Only a profile that is actually complete is replayed. An incomplete
      // one means the write has not round-tripped yet, and re-deriving the
      // wizard's own state from it would put the modal the user just finished
      // straight back on screen; the next snapshot re-evaluates everything
      // from scratch anyway.
      const data = this.studentInfo;
      if (
        data &&
        typeof data === "object" &&
        "name" in data &&
        "selectedLanguage" in data
      ) {
        this.applyStudentInfo(data);
      }
      // Deliberately after the replay, and not awaited: on web the launch is a
      // full-page redirect, so anything queued behind it would never run, and
      // the dashboard has to be in a usable state before the user leaves for
      // (or comes back from) a payment sheet.
      //
      // Also deliberately NOT conditioned on that replay. Step 2's write is
      // fire-and-forget, so a slow or failed one leaves the snapshot
      // incomplete here, and the choice is between honouring the pick against
      // a half-written profile or dropping it. Honouring it wins: the account
      // is not lost, it re-enters the wizard at step 2 on the next load (with
      // the plan step skipped, since billing now exists) and keeps the
      // subscription it paid for. Dropping the pick would leave a user who
      // chose a paid plan with no purchase and no explanation.
      this.launchPlanCheckout(planSelection);
    },
    // Translate the wizard's pick into the exact key each rail wants, or into
    // nothing. Returning null is the only outcome for free, for a plan id this
    // build does not sell, and for an unrecognised cycle: getPlanDetailsById
    // answers unknown ids with the free plan, and turning that fallback into a
    // charge is the failure worth designing against.
    resolvePlanCheckout(planId, cycle) {
      const cycleKeys = Object.prototype.hasOwnProperty.call(
        CHECKOUT_CYCLES,
        cycle
      )
        ? CHECKOUT_CYCLES[cycle]
        : null;
      if (!cycleKeys) return null;
      const plan = Object.prototype.hasOwnProperty.call(
        pricingStore.plans,
        planId
      )
        ? pricingStore.plans[planId]
        : null;
      if (!plan) return null;
      const lookupKey = plan[cycleKeys.lookupKeyField];
      const basePlanId = pricingStore.getBasePlanId(planId, cycleKeys.iapCycle);
      // The free plan has neither, which is what makes "picked free" and
      // "picked something unsellable" the same inert outcome.
      if (!lookupKey && !basePlanId) return null;
      return { lookupKey, basePlanId };
    },
    // A paid pick reaches the SAME checkout the Account upgrade buttons and
    // the marketing hand-off use. Kept async and self-contained so the caller
    // (an event handler) never has to think about a rejection.
    async launchPlanCheckout(planSelection) {
      if (!planSelection || typeof planSelection !== "object") return;
      const keys = this.resolvePlanCheckout(
        planSelection.plan,
        planSelection.cycle
      );
      if (!keys) return;

      // The same session state the wizard's plan step reads. It is checked
      // again here because a checkout can be launched between the step-2
      // submit that decided to show the step and the CTA that ends it. Erring
      // toward "already launched" costs a purchase the user can still make
      // from Account; the reverse mistake charges them twice.
      if (wasCheckoutLaunched(authStore.userId)) return;

      // Evaluated HERE rather than when the step rendered. On iOS
      // getPaymentStrategy fails closed to iap-only until StoreKit resolves
      // the storefront, and the wizard is exactly the delay that lets it land.
      const strategy = platformBilling.getPaymentStrategy(
        platformBilling.getUserCountry(this.studentInfo || {})
      );

      // Recorded before the launch and regardless of how it goes, matching
      // checkPendingSubscription: "a checkout was launched" cannot become
      // untrue, and the outcome arrives as billing/features, not as a reset.
      markCheckoutLaunched(authStore.userId);
      try {
        if (strategy === "iap-only" && keys.basePlanId) {
          // The boolean is deliberately dropped. false covers cancelled,
          // failed AND store-completed-but-not-yet-validated orders, and
          // iapUtils.purchase has already said which — only it knows. A
          // verdict here would be wrong for the third case, so the user simply
          // lands on the dashboard and the entitlement appears if and when the
          // receipt validates.
          await iapUtils.purchase(keys.basePlanId);
        } else {
          // startCheckout throws on a non-ok response and on a missing
          // checkout URL, so reaching the next line means the redirect (or
          // the native browser launch) really was handed a URL. Never treat
          // "no exception" as more than that.
          await stripeUtils.startCheckout(keys.lookupKey);
        }
      } catch (error) {
        // Loud on purpose: silence here reads as "my purchase went through".
        console.error("Error starting onboarding checkout:", error);
        // i18n.global.t, not $t: this view renders no copy of its own (a
        // literal-copy test pins that it has zero $t calls), and every other
        // imperative toast in the app resolves its message the same way —
        // MultiLanguageUpsellModal raises this exact key on this exact
        // failure.
        useToast().showToast(
          i18n.global.t("errors.billing.upgradeStartFailed"),
          "error"
        );
      }
    },
    handleConflictResolved() {
      this.showLanguageConflictModal = false;
      // The studentInfo watcher will re-run with updated data
    },
    handleSidebarCollapse(collapsed) {
      this.sidebarCollapsed = collapsed;
    },
    // Re-derived from every users/{uid} snapshot so the explainer follows the
    // account rather than the tab: a dismissal on the phone clears the flag
    // here too, and a grant that lands mid-session opens it without a reload.
    //
    // hasFreebiesGrant, not "the node exists": it means "a bonus that survives
    // the same coercion checkFreeUserLimit applies", so a node the server
    // refuses can never announce a bonus the user cannot spend. This view does
    // no parsing of its own, by design (there is one client-side derivation).
    //
    // No null guard of its own: a deleted (or not-yet-created) account streams
    // null, and hasFreebiesGrant already rejects a null or scalar snapshot.
    // `granted` is therefore also the null check for the property read below,
    // which is why it has to stay the FIRST term of that expression.
    evaluateFreebiesGrant(userData) {
      const bonus = hasFreebiesGrant(userData) ? getFreebiesBonus(userData) : null;
      // Announceable, not merely valid. Two extra conditions on top of the
      // shared helper, both about what this modal SAYS rather than what the
      // node is worth:
      //
      // 1. Free accounts only. `features` present is exactly how the server
      //    decides a user is paid (helpers/users.isFreeUser), and the paid path
      //    never consults `freebies`. A subscriber carrying a leftover grant
      //    would otherwise be told about "your free account" while on a plan
      //    where the bonus does nothing.
      // 2. Both halves present. The copy names a chat count AND a lesson
      //    count, and getFreebiesBonus deliberately accepts an asymmetric node
      //    ("an absent key is simply a zero bonus"), which would render
      //    "0 extra lessons". The backfill only ever writes both, so this
      //    costs nothing real and keeps the sentence honest.
      //
      // `bonus !== null` stays FIRST: it is also the null-snapshot guard, and
      // isFreeAccount reads a property off userData.
      const announceable =
        bonus !== null &&
        bonus.chats > 0 &&
        bonus.lessons > 0 &&
        isFreeAccount(userData);
      this.freebiesBonus = announceable ? bonus : null;
      // Any truthy stored value counts as seen. The flag is client-writable
      // (precedent: onboarding/geoMismatchModalShown) and the worst outcome of
      // being generous is that a tamperer suppresses their own explainer,
      // while being strict would re-show it to someone who already dismissed
      // it from a client that wrote a different truthy value.
      this.freebiesEligible =
        announceable &&
        !userData.onboarding?.freebiesModalShown &&
        !this.freebiesDismissed;
    },
    onFreebiesModalUpdate(value) {
      // Modal.vue emits on backdrop click, Esc, the close button, the mobile
      // swipe-down and the Android back button as well as the CTA; all of them
      // are dismissals. It never emits true.
      if (!value) this.dismissFreebiesModal();
    },
    // Optimistic like OnboardingWizard.markMismatchShown: the local state drops
    // first so the explainer cannot flicker back while the write is in flight,
    // and a failed write only costs the user seeing it again next session.
    async dismissFreebiesModal() {
      if (!this.freebiesEligible) return;
      this.freebiesEligible = false;
      // Latched before the write, and regardless of whether it succeeds: the
      // user asked for this explainer to go away, and re-showing it because
      // RTDB rejected the flag would be worse than showing it once more on the
      // next visit.
      this.freebiesDismissed = true;
      // The account this view is DISPLAYING, not whatever authStore holds now.
      // The users/{uid} subscription is bound once per mount and does not
      // follow a uid change (auth can change in another tab while this layout
      // stays mounted), so reading authStore here could write the seen flag to
      // an account that never saw the explainer and permanently suppress it.
      //
      // Always set: mounted() only subscribes when there is a uid, and nothing
      // can be eligible before that subscription has delivered a snapshot.
      const userId = this._studentInfoUserId;
      try {
        await update(ref(getDatabase(), `users/${userId}/onboarding`), {
          freebiesModalShown: true,
        });
      } catch (error) {
        console.warn("Failed to persist freebiesModalShown flag:", error);
      }
    },
    watchStudentInfo() {
      const db = getDatabase();
      // Replace, never stack, and keep the unsubscribe so unmounted() can
      // detach it; otherwise the listener outlives the view and keeps reading
      // users/{uid} after sign-out, where the read is no longer permitted
      // (bug-hunt M3).
      if (this._unsubscribeStudentInfo) {
        this._unsubscribeStudentInfo();
        this._unsubscribeStudentInfo = null;
      }
      // The explainer's state belongs to whichever subscription produced it,
      // so every (re)bind starts fresh: a dismissal recorded against the
      // previous binding must not suppress the new one, and re-deriving from
      // the next snapshot is authoritative anyway. Unconditional rather than
      // "only when the uid changed" so there is no branch here that nothing
      // can exercise.
      this.freebiesDismissed = false;
      this.freebiesEligible = false;
      this.freebiesBonus = null;
      // Same reasoning for the wizard guard: it belongs to the subscription
      // that opened it. A guard left standing across a re-bind would withhold
      // the new account's snapshots forever, since the wizard that would have
      // cleared it is gone.
      this.onboardingInFlight = false;
      // Note: the checkout latch is deliberately NOT reset here. It belongs to
      // the person, not to this binding or this mount, and it survives on
      // purpose across the reload a web checkout performs. It carries the uid
      // it was set for, so a different account reads it as absent without
      // anything having to notice the switch (checkoutSession.js).
      this._studentInfoUserId = authStore.userId;
      this._unsubscribeStudentInfo = onValue(
        ref(db, "users/" + authStore.userId),
        (snapshot) => {
          const data = snapshot.val();
          this.studentInfo = data;
          // Both startup writes below re-create users/{uid} for an account the
          // backend is in the middle of deleting, which is how a deleted
          // account comes back as a stub (and, before the server-side guards,
          // how it earned a "new user" Slack post). Pending retries are timer
          // driven rather than snapshot driven, so they have to be stood down
          // explicitly — a later snapshot cannot stop them.
          if (isAccountDeletionInFlight()) {
            this.cancelStartupRetry("_regionRetryTimer");
            this.cancelStartupRetry("_timezoneRetryTimer");
            return;
          }
          this.checkAndSaveTimezone(data);
          if (data?.region?.country) {
            // The region can land without this mount's request being the one
            // that produced it (another session, or a response we never saw).
            // detectRegion unconditionally rewrites the saved country, so a
            // pending retry must be stood down rather than allowed to change
            // the account's billing rails after the fact.
            this._regionDetectDone = true;
            this.cancelStartupRetry("_regionRetryTimer");
          } else {
            this.requestRegionDetection();
          }
        },
        (error) => {
          console.error("read failed:", error);
        }
      );
    },
    // Both startup writes are server-side: the value only appears in userData
    // once the write lands, so every snapshot in between re-enters these
    // callbacks. Unguarded, that fired one request per snapshot (bug-hunt L4).
    // The guard is deliberately not "once per mount" — a first attempt that
    // fails must still be retried, or the session is stuck with the fallback
    // for as long as the SPA stays mounted. So: never two in flight, stop once
    // one succeeds, cap the attempts, and drive the retries off a timer rather
    // than the snapshot stream (a failed write produces no snapshot).
    // The cap is per mount by design: a fresh mount is a fresh session state.
    scheduleStartupRetry(timerKey, attempts, run) {
      if (
        this._isUnmounted ||
        // A write that was already awaiting when the deletion was dispatched
        // lands in its own finally and would re-arm a timer here. The retry
        // would find the flag and bail, but arming it in the first place is
        // pointless work against an account that is going away.
        isAccountDeletionInFlight() ||
        this[timerKey] ||
        attempts >= STARTUP_WRITE_MAX_ATTEMPTS
      ) {
        return;
      }
      this[timerKey] = setTimeout(() => {
        this[timerKey] = null;
        if (!this._isUnmounted) run();
      }, STARTUP_WRITE_RETRY_BASE_MS * attempts);
    },
    cancelStartupRetry(timerKey) {
      if (this[timerKey]) {
        clearTimeout(this[timerKey]);
        this[timerKey] = null;
      }
    },
    // async so that a synchronous throw anywhere below still runs the finally
    // block: leaving an in-flight flag stuck true would lock out every later
    // attempt for the lifetime of this mount.
    async requestRegionDetection() {
      // Also checked here, not only in the snapshot callback: a retry armed
      // before the deletion was dispatched fires off its own timer, and if the
      // teardown snapshot has not arrived yet (a slow deletion outlasting the
      // retry delay) nothing else would stand it down.
      if (isAccountDeletionInFlight()) return;
      if (
        this._regionDetectDone ||
        this._regionDetectInFlight ||
        (this._regionDetectAttempts || 0) >= STARTUP_WRITE_MAX_ATTEMPTS
      ) {
        return;
      }
      this._regionDetectInFlight = true;
      this._regionDetectAttempts = (this._regionDetectAttempts || 0) + 1;
      try {
        // detectRegion resolves null on any failure rather than rejecting.
        const country = await platformBilling.detectRegion();
        // Falling back to the locale-derived country silently picks the
        // user's billing rails (platformBilling.getPaymentStrategy), so a
        // failed detection is worth retrying.
        if (country) this._regionDetectDone = true;
      } catch (error) {
        console.error("Error detecting region:", error);
      } finally {
        this._regionDetectInFlight = false;
        if (!this._regionDetectDone) {
          this.scheduleStartupRetry(
            "_regionRetryTimer",
            this._regionDetectAttempts,
            () => this.requestRegionDetection()
          );
        }
      }
    },
    // async for the same reason as requestRegionDetection: Intl, getDatabase,
    // ref and update can all throw synchronously.
    async checkAndSaveTimezone(userData) {
      // Same reason as requestRegionDetection: an already-armed retry timer is
      // not reachable from the snapshot callback's cancellation.
      if (isAccountDeletionInFlight()) return;
      if (userData?.timezone) {
        this._timezoneWriteDone = true;
        this.cancelStartupRetry("_timezoneRetryTimer");
        return;
      }
      if (
        this._timezoneWriteDone ||
        this._timezoneWriteInFlight ||
        (this._timezoneWriteAttempts || 0) >= STARTUP_WRITE_MAX_ATTEMPTS
      ) {
        return;
      }
      this._timezoneWriteInFlight = true;
      this._timezoneWriteAttempts = (this._timezoneWriteAttempts || 0) + 1;
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const db = getDatabase();
        const userRef = ref(db, "users/" + authStore.userId);
        await update(userRef, { timezone });
        this._timezoneWriteDone = true;
        console.debug("Timezone saved successfully:", timezone);
      } catch (error) {
        // Left un-done on purpose: the server falls back to UTC for a missing
        // timezone when it enforces monthly limits, which moves the learner's
        // quota boundary, so this is worth retrying.
        console.error("Error saving timezone:", error);
      } finally {
        this._timezoneWriteInFlight = false;
        if (!this._timezoneWriteDone) {
          this.scheduleStartupRetry(
            "_timezoneRetryTimer",
            this._timezoneWriteAttempts,
            () => this.checkAndSaveTimezone(this.studentInfo)
          );
        }
      }
    },
    async checkPendingSubscription() {
      const pendingSubscription = localStorage.getItem(
        STORAGE_KEYS.PENDING_SUBSCRIPTION
      );
      if (pendingSubscription) {
        try {
          const plan = JSON.parse(pendingSubscription);
          console.log("found pending subscription", plan);
          localStorage.removeItem(STORAGE_KEYS.PENDING_SUBSCRIPTION);

          // Check if we should route to IAP instead of Stripe
          const userCountry = platformBilling.getUserCountry(this.studentInfo || {});
          const strategy = platformBilling.getPaymentStrategy(userCountry);

          // Recorded BEFORE the launch, and regardless of how it goes: the
          // stash is already popped, so this is the only trace that this
          // session sent the user to a checkout. Onboarding's plan step reads
          // it and steps aside rather than offering a second purchase to
          // someone already mid-purchase.
          markCheckoutLaunched(authStore.userId);

          if (strategy === "iap-only" && plan.iapBasePlanId) {
            await iapUtils.purchase(plan.iapBasePlanId);
          } else {
            await stripeUtils.startCheckout(plan.lookupKey);
          }
        } catch (error) {
          console.error("Error handling pending subscription:", error);
        }
      }
    },
  },
  async mounted() {
    authStore.checkAuthOrElseRedirect();
    if (authStore.userId) {
      // Load user's language, theme and playback-speed preferences (RTDB wins
      // over the local cache). The speed is applied here, the parent route of
      // every chat, the way the theme is; loadUserProfile has already dropped
      // it if the learner tapped the chip while the read was in flight.
      const profileUserId = authStore.userId;
      authStore.loadUserProfile().then(({ theme, audioPlaybackRate }) => {
        if (theme) useTheme().adoptStoredMode(theme);
        if (audioPlaybackRate !== null && audioPlaybackRate !== undefined) {
          adoptStoredPlaybackRate(audioPlaybackRate, profileUserId);
        }
      });

      this.checkPendingSubscription();
      this.watchStudentInfo();

      // Listen for sidebar collapse event from the Navigation component.
      // Keep the reference: removeEventListener is a silent no-op unless it is
      // handed the exact function that was added (bug-hunt M3).
      this._sidebarHandler = (event) => {
        this.handleSidebarCollapse(event.detail);
      };
      window.addEventListener("sidebar-collapsed", this._sidebarHandler);

      this._resizeHandler = () => { this.isMobile = window.innerWidth <= 768; };
      window.addEventListener("resize", this._resizeHandler);
    }
  },

  unmounted() {
    // Checked by the retry timers so a pending one cannot re-fire a request
    // against a destroyed component.
    this._isUnmounted = true;
    this.cancelStartupRetry("_regionRetryTimer");
    this.cancelStartupRetry("_timezoneRetryTimer");
    if (this._unsubscribeStudentInfo) {
      this._unsubscribeStudentInfo();
      this._unsubscribeStudentInfo = null;
    }
    if (this._sidebarHandler) {
      window.removeEventListener("sidebar-collapsed", this._sidebarHandler);
      this._sidebarHandler = null;
    }
    if (this._resizeHandler) {
      window.removeEventListener("resize", this._resizeHandler);
      this._resizeHandler = null;
    }
  },
};
</script>

<style scoped>
.dashboard {
  display: grid;
  grid-template-columns: 250px 1fr;
  min-height: 100vh;
  min-height: 100dvh;
  transition: grid-template-columns 0.2s;
  position: relative;
}

.dashboard.sidebar-collapsed {
  grid-template-columns: 64px 1fr;
}

.nav-panel {
  background-color: var(--al-surface-sunken);
  border-right: 1px solid var(--al-border);
}

.content-area {
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
  max-height: 100vh;
  max-height: 100dvh;
}

@media (max-width: 1023px) {
  .dashboard {
    grid-template-columns: 0px 1fr;
  }

  .dashboard.sidebar-collapsed {
    grid-template-columns: 0px 1fr;
  }
}

/* Mobile: add bottom padding for the tab bar */
@media (max-width: 768px) {
  .dashboard {
    grid-template-columns: 1fr;
    /* The (hidden) nav panel is a zero-height row above the content area in
       this single-column grid. When the keyboard shrinks .content-area below
       the dashboard's 100dvh (see the --app-height rule below), the default
       align-content: stretch redistributes the freed space across both auto
       rows — shoving the content area down by half the keyboard height. Pin
       rows to the top; the dashboard itself stays full-height so the surface
       color still fills the screen behind the keyboard. */
    align-content: start;
  }

  .dashboard.sidebar-collapsed {
    grid-template-columns: 1fr;
  }

  /* Both paddings come from tokens (utilities.css) because the transition
     spacers further down must reproduce exactly these two values; see the
     comment on the spacer rules. --al-content-bottom-reserve expands to
     `56px + var(--al-tab-bar-inset)`, which resolves to the natively injected
     --safe-bottom on Android (webview env() values are unreliable there),
     16px on iOS, and the env() fallback on mobile web, so it always matches
     the BottomTabBar height exactly.
     `position: relative` is load-bearing for the transition geometry, not
     decoration: it is the only reason .content-area is the containing block
     for the absolutely positioned animating view. Drop it and the wrapper
     resolves against .dashboard (also relative, but unpadded), at which point
     the spacers stop compensating for anything and become a pure over-shift. */
  .content-area {
    padding-top: var(--al-content-top-inset);
    padding-bottom: var(--al-content-bottom-reserve);
    position: relative;
  }

  /* Keyboard-aware height: --app-height (App.vue) is the viewport minus
     whatever the keyboard covers, measured per platform. On iOS the webview
     is NOT resized by the keyboard (Capacitor resize:'none'), so a 100dvh
     scroll container can never bring keyboard-covered content (e.g. the
     Account page's feedback form) into view; sizing to --app-height restores
     scroll access. On Android 16 the webview already shrinks and --app-height
     equals the viewport, so this is a no-op there.
     `.dashboard .content-area` (not bare .content-area): the base height rule
     above has equal specificity, and the production minifier reorders
     equal-specificity duplicates — same trap as the transition rules below. */
  .dashboard .content-area {
    height: var(--app-height, 100dvh);
    max-height: var(--app-height, 100dvh);
  }

  /* Page transition animations — mobile only.
     `.content-area > ` gives these rules higher specificity than the views'
     own single-class root rules (e.g. .free-chat-view's position: relative,
     .lessons-view's base min-height: 100dvh): at equal specificity the winner
     depends on how the production minifier orders the chunks, which silently
     broke the absolute positioning in release builds. The one selector that
     now ties with these is .lessons-view's own mobile
     `.lessons-view.lessons-view { min-height: 100% }`, which the same
     minifier hazard forced to two classes — a tie with no consequence, since
     it declares the same min-height these rules do and touches nothing else
     they set.
     height/min-height 100%: a transformed wrapper becomes the containing block
     for the chat's position:fixed overlay, so the wrapper must span the full
     viewport (absolute children anchor to the padding box, so top:0 +
     height:100% does exactly that) — otherwise the mic button jumps up by the
     footer's tab-bar reservation mid-animation. */
  .content-area > .slide-left-enter-active,
  .content-area > .slide-left-leave-active,
  .content-area > .slide-right-enter-active,
  .content-area > .slide-right-leave-active {
    transition: transform var(--al-dur-medium) var(--al-ease-move),
      opacity var(--al-dur-medium) var(--al-ease-move);
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    min-height: 100%;
  }

  /* Transition-scoped safe-area spacers.

     The rule above takes the entering and leaving views out of normal flow for
     the length of the slide. An absolutely positioned child anchors to its
     containing block's PADDING box, and .content-area is that containing block
     (only because of its `position: relative` above), so `top: 0` puts the
     animating view on top of .content-area's own padding — flush with the
     physical screen top. Every child therefore renders one top inset too high
     for ~300ms and drops into place in a single unanimated frame when Vue
     removes the transition classes. The free-tier ledger card is the most
     visible victim because it is the topmost content on My Course and My Chats,
     and the leaving view suffers the mirror image of the same jump: it hops UP
     by the inset the moment the classes land.

     The fix reproduces both of .content-area's paddings INSIDE the wrapper as
     pseudo-element spacers, so the wrapper's interior geometry matches steady
     state while its outer geometry is untouched:
     - ::before restores the top inset, so content lands at
       `screen top + the view's own padding + top inset` — its normal-flow
       position — with no per-view knowledge needed.
     - ::after restores the bottom reserve, so the room between the spacers
       equals .content-area's content-box height. Without it, vertically
       centered content (FreeChatView's welcome group) drifts by half the
       bottom reserve during the slide.
     - The heights come from the same two tokens .content-area's padding
       consumes, which is the whole point of the tokens: the shell and the
       spacers cannot drift apart by editing one site.
     - The wrapper's own `top: 0; height: 100%` stays untouched. That geometry
       is what PR #573 fixed: the transformed wrapper is the containing block
       for the chat's position:fixed fullscreen overlay, so moving or shrinking
       it re-opens the mid-animation mic-button jump. Pseudo-elements live in
       the content box and do not move the padding box the overlay anchors to.
     - `flex: 0 0 auto` because .lessons-view and .free-chat-view are flex
       columns, where the spacers become flex items with the default
       flex-shrink: 1 and a height-hungry sibling could squash them. On the
       block roots (.account-view, .progress-view) it is inert.
     - The `.content-area > ` prefix is required for the same reason the
       transition rules carry it: the production minifier reorders
       equal-specificity duplicates, so these must win by specificity.

     Known residual, closed by the next commit in this stack (the spec's Unit
     2), and worth stating rather than overclaiming: this reasoning assumes a
     view root's steady-state box is .content-area's content box. That holds
     for .account-view and .progress-view, and for TOP-ANCHORED content
     everywhere — including the ledger card, which is a direct child of
     .lessons-view above .welcome-container, so it is fully corrected here. It
     does not yet hold for .lessons-view's own VERTICALLY CENTERED content
     (.welcome-header's auto block margins), because .lessons-view still
     carries a mobile `min-height: calc(100dvh - 56px)` that overshoots the
     content box, while the transition rule above replaces it with
     `min-height: 100%`. On a short My Course page the centering box is
     therefore ~(top inset + tab-bar inset)/2 shorter during the slide than at
     rest — 24px on the API 36 emulator, where it previously drifted 4px the
     other way. Removing that overshoot is exactly what Unit 2 does, after
     which both boxes are the content box and the drift is zero. Compensating
     for it here instead would mean teaching the shell each view's own
     min-height, which the spec rejected (permanent parity burden) and which
     would over-correct the moment Unit 2 lands.

     Narrower than it was: the course-progress-screen refresh made My Course's
     course-exists body top-aligned, so `.welcome-header`'s auto block margins
     now centre only the no-course welcome hero, which is the one remaining
     vertically centered case this paragraph is about. */
  .content-area > .slide-left-enter-active::before,
  .content-area > .slide-left-leave-active::before,
  .content-area > .slide-right-enter-active::before,
  .content-area > .slide-right-leave-active::before {
    content: "";
    display: block;
    flex: 0 0 auto;
    height: var(--al-content-top-inset);
  }

  .content-area > .slide-left-enter-active::after,
  .content-area > .slide-left-leave-active::after,
  .content-area > .slide-right-enter-active::after,
  .content-area > .slide-right-leave-active::after {
    content: "";
    display: block;
    flex: 0 0 auto;
    height: var(--al-content-bottom-reserve);
  }

  /* A scrolled leaving view is compensated exactly by arithmetic — the top
     spacer pushes content down by the inset while the wrapper's border box
     moves up by the same inset — but only while scrollTop is left alone.
     Chromium (the Android WebView) implements CSS scroll anchoring and adjusts
     scrollTop when layout is inserted above the visible region, which is
     precisely what the ::before spacer does; WebKit implements no anchoring, so
     this is inert on iOS.
     Standing rule, deliberately NOT scoped to the -active classes: an
     interrupted transition (tapping a third tab mid-slide) takes the element
     through a forced reflow BETWEEN class swaps, a window in which an
     -active-scoped opt-out would not apply and anchoring would be re-enabled on
     a scrolled scroller. It matters for .lessons-view and .account-view, whose
     roots are their own scrollers. Cost: scroll anchoring is off inside these
     views on mobile generally, which is acceptable because they load content by
     appending below rather than inserting above — the only case anchoring
     helps. */
  .content-area > * {
    overflow-anchor: none;
  }

  .slide-left-enter-from { transform: translateX(100%); opacity: 0.8; }
  .slide-left-leave-to { transform: translateX(-30%); opacity: 0; }
  .slide-right-enter-from { transform: translateX(-30%); opacity: 0.8; }
  .slide-right-leave-to { transform: translateX(100%); opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  /* Same `.content-area > ` prefix as the mobile transition rules above —
     without it this override loses on specificity and the slide would still
     animate for users who requested reduced motion. */
  .content-area > .slide-left-enter-active,
  .content-area > .slide-left-leave-active,
  .content-area > .slide-right-enter-active,
  .content-area > .slide-right-leave-active {
    transition: none;
  }
}

</style>
